"use client"

import { useState } from "react"
import { ArrowLeft, Building, Mail, MapPin, Send } from "lucide-react"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"
import { useGetContacts, useGetEmails } from "openapi/api/endpoints"
import type { Contact } from "openapi/api/model"

import { PersonContactDetails } from "./person-contact-details"
import { PersonEmailDialog } from "./person-email-dialog"
import { PersonNotes } from "./person-notes"
import { PersonTouchpoints } from "./person-touchpoints"

type ContactWithCompany = Contact & {
  companies?: {
    name?: string | null
  } | null
}

type PersonProfileContentProps = {
  personId: string
  className?: string
  onBack?: () => void
}

function getPersonName(person: ContactWithCompany | undefined) {
  return [person?.first_name, person?.last_name].filter(Boolean).join(" ") || "Unnamed contact"
}

function getInitials(name: string) {
  return (
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "?"
  )
}

function getCompanyName(person: ContactWithCompany | undefined) {
  return person?.companies?.name ?? person?.company_name ?? ""
}

/**
 * Shared person profile surface for both the routed page and the drawer.
 * The caller owns where the profile lives; this component owns loading the
 * contact/touchpoint data and keeping the tabbed person workflow consistent.
 */
export function PersonProfileContent({ personId, className, onBack }: PersonProfileContentProps) {
  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const { data: contact, isLoading, isError, refetch } = useGetContacts({ contactId: personId })
  const {
    data: touchPoints,
    isLoading: isLoadingTouchPoints,
    isError: hasTouchPointsError,
    refetch: refetchTouchPoints,
  } = useGetEmails({ contact_id: personId })
  const person = contact?.[0] as ContactWithCompany | undefined
  const personName = getPersonName(person)
  const companyName = getCompanyName(person)
  const retryContact = () => {
    void refetch()
  }
  const retryTouchPoints = () => {
    void refetchTouchPoints()
  }

  if (isLoading) {
    return (
      <div className={cn("flex-1 p-6", className)}>
        <div className="text-sm text-muted-foreground">Loading contact...</div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={cn("flex-1 space-y-4 p-6", className)}>
        {onBack && (
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
        <Card>
          <CardContent className="flex flex-col gap-4 p-6 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between">
            <span>Could not load this contact.</span>
            <Button variant="outline" onClick={retryContact}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!person) {
    return (
      <div className={cn("flex-1 space-y-4 p-6", className)}>
        {onBack && (
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            This contact is unavailable in the current context.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className={cn("flex-1 space-y-6 p-6", className)}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center space-x-4">
          {onBack && (
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="flex min-w-0 items-center space-x-4">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="text-lg">{getInitials(personName)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h1 className="truncate text-3xl font-bold tracking-tight">{personName}</h1>
              <p className="truncate text-muted-foreground">
                {person.contact_type?.replace("_", " ") || "Contact"}
                {companyName ? ` at ${companyName}` : ""}
              </p>
            </div>
          </div>
        </div>

        <Button onClick={() => setEmailDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700 lg:self-auto">
          <Send className="h-4 w-4 mr-2" />
          Send Email
        </Button>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="general">General Info</TabsTrigger>
          <TabsTrigger value="touchpoints">Touchpoints History</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{person.email || "No email"}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <span>{companyName || "No company"}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{person.title || "No title"}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-muted-foreground">Type:</span>
                    <Badge className="ml-2" variant={person.company_id ? "secondary" : "default"}>
                      {person.company_id ? "Hiring Manager" : "Candidate"}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Total Touch Points:</span>
                    {isLoadingTouchPoints ? (
                      <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
                    ) : hasTouchPointsError ? (
                      <span className="ml-2 inline-flex items-center gap-2">
                        <span className="text-sm text-destructive">Could not load</span>
                        <Button type="button" variant="outline" size="sm" className="h-7 px-2" onClick={retryTouchPoints}>
                          Retry
                        </Button>
                      </span>
                    ) : (
                      <span className="ml-2 font-medium">{touchPoints?.length ?? 0}</span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <PersonContactDetails personId={personId} />

          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <PersonNotes personId={personId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="touchpoints" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Touchpoints History</CardTitle>
            </CardHeader>
            <CardContent>
              <PersonTouchpoints
                personId={personId}
                touchPoints={touchPoints}
                isLoading={isLoadingTouchPoints}
                isError={hasTouchPointsError}
                onRetry={retryTouchPoints}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PersonEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        personId={personId}
        person={{
          name: personName,
          email: person.email ?? "",
          position: person.title ?? "",
          company: companyName,
        }}
      />
    </div>
  )
}
